/**
 * FundMatch React Query Hooks
 * Custom hooks for FundMatch API endpoints with caching and error handling
 */
import { useQuery } from '@tanstack/react-query';
import {
  getFundMatchInfo,
  searchGrants,
  getGrantMatches,
  getCharityMeasures,
  listCharities,
  getDefaultSearchMatches,
} from '../services/fundmatch';

/**
 * Hook to get FundMatch product info
 */
export const useFundMatchInfo = () => {
  return useQuery({
    queryKey: ['fundmatch', 'info'],
    queryFn: getFundMatchInfo,
  });
};

/**
 * Hook to list grants with filters
 * @param {Object} filters - Filter parameters
 * @param {boolean} enabled - Whether to enable the query
 */
export const useListGrants = (filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ['fundmatch', 'grants', filters],
    queryFn: () => listGrants(filters),
    enabled,
  });
};

/**
 * Hook to search grants by keyword
 * @param {string} query - Search query
 * @param {boolean} enabled - Whether to enable the query
 */
export const useSearchGrants = (query, enabled = true) => {
  return useQuery({
    queryKey: ['fundmatch', 'search', query],
    queryFn: () => searchGrants(query),
    enabled: enabled && !!query,
  });
};

/**
 * Hook to get grant matches for a charity
 * @param {string} charityBN - Charity Business Number
 * @param {string} [projectDescription] - Optional project description to activate HyDE + GraphRAG
 * @param {boolean} enabled - Whether to enable the query
 */
export const useGrantMatches = (charityBN, projectDescription, enabled = true) => {
  return useQuery({
    queryKey: ['fundmatch', 'matches', charityBN, projectDescription || null],
    queryFn: () => getGrantMatches(charityBN, projectDescription),
    enabled: enabled && !!charityBN,
  });
};

/**
 * Hook to get 8 measures for a charity
 * @param {string} charityBN - Charity Business Number
 * @param {boolean} enabled - Whether to enable the query
 */
export const useCharityMeasures = (charityBN, enabled = true) => {
  return useQuery({
    queryKey: ['fundmatch', 'measures', charityBN],
    queryFn: () => getCharityMeasures(charityBN),
    enabled: enabled && !!charityBN,
  });
};

/**
 * Hook to get Tier A default search matches (v9.4 province-aware)
 * @param {string} charityBN - Charity Business Number
 * @param {boolean} enabled - Whether to enable the query
 */
export const useDefaultSearchMatches = (charityBN, enabled = true) => {
  return useQuery({
    queryKey: ['v94', 'default-search', charityBN],
    queryFn: () => getDefaultSearchMatches(charityBN),
    enabled: enabled && !!charityBN,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to list charities with filters
 * @param {Object} filters - Filter parameters
 * @param {boolean} enabled - Whether to enable the query
 */
export const useCharities = (filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ['fundmatch', 'charities', filters],
    queryFn: () => listCharities(filters),
    enabled,
  });
};

// ─── Competition hooks ────────────────────────────────────────────────────────
import { getCompetitionSummary, getDirectCompetitors, getCompetitionLandscape } from '../services/fundmatch';

export const useCompetitionSummary = (bn, enabled = true) =>
  useQuery({
    queryKey: ['competition', 'summary', bn],
    queryFn: () => getCompetitionSummary(bn),
    enabled: enabled && !!bn,
    staleTime: 10 * 60 * 1000,
  });

export const useDirectCompetitors = (bn, limit = 20, enabled = true) =>
  useQuery({
    queryKey: ['competition', 'direct', bn, limit],
    queryFn: () => getDirectCompetitors(bn, limit),
    enabled: enabled && !!bn,
    staleTime: 10 * 60 * 1000,
  });

export const useCompetitionLandscape = (bn, enabled = true) =>
  useQuery({
    queryKey: ['competition', 'landscape', bn],
    queryFn: () => getCompetitionLandscape(bn),
    enabled: enabled && !!bn,
    staleTime: 10 * 60 * 1000,
  });

export const useGrantCompetition = (bn, grantId, enabled = true) =>
  useQuery({
    queryKey: ['competition', 'grant', bn, grantId],
    queryFn: () => import('../services/fundmatch').then(m => m.getGrantCompetition(bn, grantId)),
    enabled: enabled && !!bn && !!grantId,
    staleTime: 15 * 60 * 1000,
  });
